import { useEffect } from 'react';
/**
 * Requests the host page's suggested prompts / smart summary as soon as the page identity and
 * client AI config are known.
 *
 * This has to live outside the conversation UI: the shopper widget only mounts <Conversation />
 * once the assistant is expanded, but Product Answers render on the host page whether or not the
 * shopper ever opens the popup. The store owns in-flight/success deduplication, so several callers
 * requesting the same page is safe.
 */
const usePageAdditionalDataRequest = (store) => {
    const isProductPageByPath = () => {
        if (typeof window === 'undefined')
            return false;
        const productPagePath = store.clientAIConfig?.productPagePath || '/product';
        const escapedPath = productPagePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const productRegex = new RegExp(`${escapedPath}(?:s)?/([^/?]+)`, 'i');
        return productRegex.test(window.location.pathname);
    };
    const isProductPage = store.customerContext?.currentPage?.isProduct || isProductPageByPath();
    const isCollectionPage = Boolean(store.customerContext?.currentPage?.isCollection)
        && !isProductPage;
    useEffect(() => {
        const config = store.clientAIConfig;
        const shouldCallProduct = isProductPage && (config?.productPageSuggestedPromptsEnabled
            || config?.productPageSmartSummaryEnabled
            || config?.productInsertMetadataEnabled);
        const shouldCallCategory = isCollectionPage && (config?.categoryPageSuggestedPromptsEnabled
            || config?.categoryPageSmartSummaryEnabled
            || config?.categoryInsertMetadataEnabled);
        if (shouldCallProduct || shouldCallCategory) {
            const location = typeof window !== 'undefined' ? window.location?.href : '';
            const currentPage = store.customerContext?.currentPage;
            let pageKey = '';
            if (isProductPage) {
                pageKey = currentPage?.product?.id
                    || currentPage?.product?.handle
                    || currentPage?.url
                    || currentPage?.title
                    || location;
            }
            else if (isCollectionPage) {
                pageKey = currentPage?.collection?.id
                    || currentPage?.collection?.handle
                    || currentPage?.url
                    || currentPage?.title
                    || location;
            }
            if (pageKey && store.lastSuggestedPromptsKey === pageKey) {
                return;
            }
            store.requestPageAdditionalData?.(location);
        }
    }, [
        store,
        isProductPage,
        isCollectionPage,
        store.clientAIConfig?.productPageSuggestedPromptsEnabled,
        store.clientAIConfig?.categoryPageSuggestedPromptsEnabled,
        store.clientAIConfig?.productPageSmartSummaryEnabled,
        store.clientAIConfig?.categoryPageSmartSummaryEnabled,
        store.clientAIConfig?.productInsertMetadataEnabled,
        store.clientAIConfig?.categoryInsertMetadataEnabled,
        store.customerContext?.currentPage?.product?.id,
        store.customerContext?.currentPage?.product?.handle,
        store.customerContext?.currentPage?.collection?.id,
        store.customerContext?.currentPage?.collection?.handle,
        store.customerContext?.currentPage?.url,
        store.customerContext?.currentPage?.title,
    ]);
    return { isProductPage, isCollectionPage };
};
export default usePageAdditionalDataRequest;
//# sourceMappingURL=usePageAdditionalDataRequest.js.map